import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { css } from '@emotion/css'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { SearchIcon, SortDesc, XIcon } from 'lucide-react'

import { UpdateQuery } from '~/dataProcessor/hooks/global/router'
import { useApiKey } from '~/dataProcessor/hooks/user'
import { Input } from '~/design-system/atom/input'
import { SimpleDropdown } from '~/ui/components/custom/dropdown/simple'
import { borderAndShadowCSS } from '~/ui/constants/style/common'
import { CodeWInstructionModal } from '~/ui/containers/codeModal'
import { capitalizeFirstLetter } from '~/utils/js'
import { LOCAL_STORAGE_KEYS, storage } from '~/utils/localStorage'
import { CodeButton } from '../connector'

interface CategoryItem {
  title: string
  key: string
}

interface FilterBarProps {
  categoriesList: CategoryItem[]
  appCountByCategory: Record<string, number>
}

export const APP_SORT_OPTIONS = [
  { label: 'Newest', value: 'newest' },
  { label: 'Alphabetically', value: 'alphabet' },
  { label: 'Usage', value: 'usage' },
  { label: 'Actions Count', value: 'actions' },
]

export const DEFAULT_APP_SORT_BY_OPTION = APP_SORT_OPTIONS[2]!.value

export const FilterBar: React.FC<FilterBarProps> = React.memo(
  ({ categoriesList, appCountByCategory }) => {
    const router = useRouter()
    const updateQuery = UpdateQuery()
    const searchInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus()
      }
    }, [])

    const { search, category = 'all' } = router.query

    const hasCustomCategory = useMemo(
      () => Object.keys(appCountByCategory).includes('custom'),
      [appCountByCategory],
    )

    const categorySorted = useMemo(() => {
      return hasCustomCategory
        ? ['all', 'custom', 'ai-machine-learning']
        : ['all', 'ai-machine-learning', 'developer-tools-devops']
    }, [hasCustomCategory])

    const filteredCategoryList = useMemo(() => {
      return categoriesList
        .filter((category) => categorySorted.includes(category.key))
        .sort(
          (a, b) =>
            categorySorted.indexOf(a.key) - categorySorted.indexOf(b.key),
        )
    }, [categoriesList, categorySorted, appCountByCategory])

    const renderCategoryItem = useCallback(
      (categoryItem: CategoryItem) => {
        const isActive = categoryItem.key === category
        const categoryCount = appCountByCategory[categoryItem.key]

        return (
          <div
            onClick={() => updateQuery('category', categoryItem.key)}
            key={categoryItem.key}
            className={clsx(
              'ds-border-blue-400 hover:ds-border-blue-400 flex cursor-pointer items-center rounded-[8px] border-[.7px] bg-[#fff] !px-[9px] py-[4px] text-[13px] font-[400] tracking-[.3px] text-black-400 hover:bg-[rgba(126,181,240,0.11)] ',
              isActive && 'ds-border-blue-500 !bg-[#faefff] text-[#541971] ',
              borderAndShadowCSS,
              css`
                ${isActive &&
                `background-color: rgba(126, 181, 240, 0.11) !important; outline-width: 1px !important; outline-offset: 0px !important; outline-color: hsla(212, 85%, 53%, 1) !important;`}
              `,
            )}
          >
            <span className="mt-[2px]">
              {capitalizeFirstLetter(categoryItem.title || '')}
            </span>
            <div className="ml-1 flex h-[20px] w-[24px] items-center justify-center rounded-[8px] border-[0.5px] border-[#d0cdcd] bg-[#fff] text-[11px] font-[500] text-black-600">
              {categoryCount || '-'}
            </div>
          </div>
        )
      },
      [category, appCountByCategory, updateQuery],
    )
    const renderDropdown = useCallback(() => {
      return (
        <SimpleDropdown
          options={
            categoriesList
              .filter((category) => appCountByCategory[category.key])
              .filter((category) => !categorySorted.includes(category.key))
              .map((category) => ({
                name: `${capitalizeFirstLetter(
                  category.title.replaceAll('_', ' ').replaceAll('-', ' '),
                )} ${appCountByCategory[category.key] ? `(${appCountByCategory[category.key]})` : ''}`,
                value: category.key,
                rightIcon: appCountByCategory[category.key] ? (
                  <span className="text-[10px] text-[#616161]">
                    {appCountByCategory[category.key]}
                  </span>
                ) : null,
              })) || []
          }
          onChange={(value) => updateQuery('category', value as string)}
          value={(category as string) || ''}
          size={32}
          placeholder="Category"
          className="w-[100px] rounded-[8px]"
        />
      )
    }, [categoriesList, appCountByCategory, category, updateQuery])

    const {
      sortBy = storage.get(
        LOCAL_STORAGE_KEYS.APP_SORT_BY,
        DEFAULT_APP_SORT_BY_OPTION,
      ),
    } = router.query

    const renderSortByFilter = useCallback(() => {
      return (
        <div className="flex items-center gap-2">
          <SortDesc
            height={13}
            width={13}
            color="#6E6E6E"
            className="mx-[5px]"
          />
          <SimpleDropdown
            className="w-[140px] rounded-[8px]"
            options={APP_SORT_OPTIONS.map((option) => ({
              name: option.label,
              value: option.value,
            }))}
            onChange={(value) => {
              // @TODO- Create constant for query params by screens
              updateQuery('sortBy', value as string)
              storage.set(LOCAL_STORAGE_KEYS.APP_SORT_BY, value as string)
            }}
            value={sortBy as string}
          />
        </div>
      )
    }, [sortBy])

    const renderSearchInput = useCallback(
      () => (
        <Input
          className={clsx(
            'min-w-[280px]',
            'ds-border-gray-500 hover:ds-border-blue-600 flex cursor-pointer items-center rounded-[8px] border-[.5px] bg-[#fff] !px-[24px] py-[2px] py-[4px] pl-[28px] !text-[13px] text-[13px] !font-[500] font-[500] tracking-[.1px] tracking-[.3px] text-black-400 text-black-400',
            inputCSS,
          )}
          ref={searchInputRef}
          placeholder="Search All Apps"
          type="text"
          size={32}
          onChange={(e) => updateQuery('search', e.target.value)}
          rightIcon={
            !!search && (search as string)?.length > 0 ? (
              <XIcon
                height={13}
                width={13}
                color="grey"
                strokeWidth={1.6}
                fill={'#6E6E6E'}
                className="ml-[-8px] cursor-pointer"
                onClick={() => updateQuery('search', '')}
              />
            ) : null
          }
          value={search || ''}
          icon={
            <SearchIcon
              strokeWidth={1.6}
              height={13}
              width={13}
              color="#6E6E6E"
              className="ml-[-5px]"
            />
          }
        />
      ),
      [search, updateQuery],
    )

    return (
      <div
        className={clsx(
          'flex w-full items-center gap-2 border-b-[.5px] border-b-[#DBDBDB] px-6 py-[8px]',
          css`
            background-color: #fff;
            position: fixed;
            width: calc(100vw - 266px);
            z-index: 10;
          `,
        )}
      >
        {renderSearchInput()}
        <div className="flex gap-2">
          <div className="flex gap-2">
            {filteredCategoryList.slice(0, 4).map(renderCategoryItem)}
          </div>
          <div className="flex gap-4">{renderDropdown()}</div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <ShowAppsCode />
          {renderSortByFilter()}
        </div>
      </div>
    )
  },
)

const inputCSS = css`
  box-shadow:
    lch(0 0 0 / 0.02) 0px 0px 4px -1px,
    lch(0 0 0 / 0.02) 0px 0px 2px 2px;
  font-size: 12px !important;
  font-weight: 400 !important;
  padding-left: 28px !important;
  letter-spacing: 0.1px;
  ::placeholder {
    color: #545454 !important;
  }
`

FilterBar.displayName = 'FilterBar'

export const sampleCodeBlockData = {
  content: {
    python: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for getting all apps',
          blocks: [
            {
              heading: 'Fetch apps',
              text: `from composio import ComposioToolSet, App

toolset = ComposioToolSet(api_key={{api_key}})
apps = toolset.get_apps()
print(apps)
`,
              language: 'python',
            },
          ],
        },
      },
    ],
    javascript: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for getting all apps',
          blocks: [
            {
              heading: 'Fetch apps',
              text: `import {Composio} from "composio-core";
// get composio client
const composio = new Composio({apiKey: {{api_key}}});

const apps = await composio.apps.list()
`,
              language: 'javascript',
            },
          ],
        },
      },
    ],
  },
}

const ShowAppsCode = () => {
  const [showCode, setShowCode] = useState(false)

  const apiKey = useApiKey()

  const transformedData = useMemo(() => {
    // transform data of sampleCodeBlockData
    const data = cloneDeep(sampleCodeBlockData)

    for (const language of Object.keys(data.content)) {
      // @ts-expect-error - this is okay for now
      for (const languageItem of data.content[language]) {
        //replace blocks with api key and integration id
        const { blocks } = languageItem.content

        if (!blocks) continue

        for (const block of blocks) {
          block.text = block.text.replace('{{api_key}}', `"${apiKey}"`)
        }
      }
    }

    return data
  }, [apiKey])

  return (
    <div>
      <CodeButton setShowCode={setShowCode} />
      {showCode && (
        <CodeWInstructionModal
          title="Fetch apps with Our SDKs"
          data={transformedData}
          setOpen={() => setShowCode(false)}
        />
      )}
    </div>
  )
}
